import { Component, inject, computed, signal, ViewChild, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { FieldService } from '../../../core/services/field.service';
import { CustomerService } from '../../../core/services/customer.service';
import { Field } from '../../../core/models/field.model';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';

@Component({
  selector: 'app-fields',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, ConfirmModalComponent, MaxLengthDirective],
  templateUrl: './fields.component.html',
  styleUrls: ['./fields.component.scss']
})
export class FieldsComponent implements OnInit {
  @ViewChild('confirmModal') confirmModal!: ConfirmModalComponent;

  private fieldService = inject(FieldService);
  private customerService = inject(CustomerService);
  private router = inject(Router);

  ngOnInit(): void {
    // Refresh fields from API to get fresh hasBoundary/agremoFieldId status
    this.fieldService.loadFields();
  }

  // Search and filter
  searchQuery = signal('');
  selectedCropType = signal<string>('');
  selectedState = signal<string>('');
  selectedCustomerId = signal<string>('');

  // Field to delete
  fieldToDelete: Field | null = null;

  // Computed signals for filtered data
  readonly fields = computed(() => {
    const allFields = this.fieldService.fields();
    const query = this.searchQuery().toLowerCase();
    const crop = this.selectedCropType();
    const state = this.selectedState();
    const customerId = this.selectedCustomerId();

    return allFields.filter(field => {
      const matchesSearch = !query ||
        field.name.toLowerCase().includes(query) ||
        field.address?.toLowerCase().includes(query) ||
        field.city?.toLowerCase().includes(query);

      const matchesCrop = !crop || field.cropType === crop;
      const matchesState = !state || field.state === state;
      const matchesCustomer = !customerId || field.customerId === customerId;

      return matchesSearch && matchesCrop && matchesState && matchesCustomer;
    });
  });

  // Get unique crop types for filter dropdown
  readonly cropTypes = computed(() => {
    const allFields = this.fieldService.fields();
    const types = new Set(allFields.map(f => f.cropType));
    return Array.from(types).sort();
  });

  // Get unique states for filter dropdown
  readonly states = computed(() => {
    const allFields = this.fieldService.fields();
    const stateSet = new Set(allFields.map(f => f.state).filter(Boolean) as string[]);
    return Array.from(stateSet).sort();
  });

  // Get unique customers for filter dropdown
  readonly customerOptions = computed(() => {
    return this.customerService.getCustomerOptions();
  });

  // Computed stats
  readonly totalAcreage = computed(() => {
    return this.fields().reduce((sum, f) => sum + f.acreage, 0);
  });

  readonly fieldCount = computed(() => this.fields().length);

  addField(): void {
    this.router.navigate(['/logbook/fields/add']);
  }

  editField(field: Field): void {
    this.router.navigate(['/logbook/fields', field.id, 'edit']);
  }

  deleteField(field: Field): void {
    this.fieldToDelete = field;
    this.confirmModal.open({
      title: 'Delete Field',
      message: `Are you sure you want to delete "${field.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonClass: 'btn-danger',
      icon: 'bi-trash'
    });
  }

  onDeleteConfirmed(): void {
    if (this.fieldToDelete) {
      this.fieldService.deleteField(this.fieldToDelete.id);
      this.fieldToDelete = null;
    }
  }

  onDeleteCancelled(): void {
    this.fieldToDelete = null;
  }

  viewOnMap(field: Field): void {
    if (field.latitude && field.longitude) {
      const url = `https://www.google.com/maps?q=${field.latitude},${field.longitude}`;
      window.open(url, '_blank');
    }
  }

  formatCoordinates(lat: number | null, lng: number | null): string {
    if (lat === null || lng === null) {
      return 'Not set';
    }
    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  }

  clearFilters(): void {
    this.searchQuery.set('');
    this.selectedCropType.set('');
    this.selectedState.set('');
    this.selectedCustomerId.set('');
  }

  // Get customer name by ID
  getCustomerName(customerId: string | undefined): string {
    if (!customerId) return '';
    const customer = this.customerService.getCustomerById(customerId);
    return customer?.name || '';
  }
}
